import { SET_COUNTRY, SET_LANGUAGE } from '@/utils/store/mutationTypes'
import requestIp from 'request-ip'
import cookieConfig from '@/config/cookie'
import { CCOUNTRY, CLOCALE, CHATBOTLOCALE } from '@/utils/cookie/cookieTypes'
import { logError } from '@/utils/general'
import { IS_AUTHENTICATED, USER_COUNTRY } from '@/utils/store/getterTypes'
import { getCookieDomain } from '@/utils/cookie'

export default async function ({ isHMR, app, store, params, route, redirect, error }) {
  const defaults = {
    locale: app.i18n.fallbackLocale,
    country: store.state.auth.loggedIn ? store.getters[`auth/${USER_COUNTRY}`] : app.$cookies.get(CCOUNTRY) || store.state.app.defaultCountry
  }
  // If middleware is called from hot module replacement, ignore it
  if (isHMR) {
    return
  }

  if (!isValidLangParam({ store, params, defaults })) {
    const routeExists = app.router.options.routes.some(r => r.path.replace('/:lang', '').replace('?', '') === route.path.replace(/\/$/, '')) || (route.name !== 'lang' && route.matched && route.matched.length)
    const { locale, country } = await getData({ app, store, defaults })

    if (hasOnlyValidCountryParam({ store, params })) {
      // In this if clause params.lang is the country eg: 'gb'
      if (route.fullPath.replace(`/${params.lang}`, '').length > 0) {
        return redirect(`/${locale}-${params.lang}${route.fullPath.replace(`/${params.lang}`, '')}`)
      }

      return redirect(`/${locale}-${params.lang}${route.fullPath}`)
    }

    if (!routeExists && route.name !== 'lang') { // exclude routes when name === lang because those are handled by _specialDesigns middleware
      return error('Page not found', 404)
    }

    return redirect(`/${locale}-${country}${route.fullPath}`)
  }

  const [locale, country] = params.lang.split('-')

  if (store.getters[`auth/${IS_AUTHENTICATED}`] && country !== store.getters[`auth/${USER_COUNTRY}`]) {
    const slug = route.fullPath.split('/').slice(2).join('/')
    return redirect(`/${locale}-${store.getters[`auth/${USER_COUNTRY}`]}/${slug}`)
  }

  const data = {
    locale: locale || defaults.locale,
    country: country || defaults.country
  }

  storeData({ app, store, data })
}

/**
 * Checks if only the country param is filled
 * eg: www.onthatass.com/fr, www.onthatass.com/gb
 *
 * @param store
 * @param params
 * @param defaults
 * @returns {boolean|boolean}
 */
const hasOnlyValidCountryParam = ({ store, params }) => {
  const onlyContainsCountryParam = !params.lang?.includes('-')
  const country = params.lang || ''

  const { app: state } = store.state

  return onlyContainsCountryParam && state.countries.includes(country)
}

/**
 * Checks if the lang param is valid
 *
 * @param store
 * @param params
 * @param defaults
 * @returns {boolean|boolean}
 */
function isValidLangParam ({ store, params }) {
  const locale = params.lang ? params.lang.split('-')[0] : false
  const country = params.lang ? params.lang.split('-')[1] : false
  const { app: state } = store.state

  return state.locales.includes(locale) && state.countries.includes(country)
}

/**
 * Gets the locale and country
 *
 * @param app
 * @param store
 * @param defaults
 * @returns {*}
 */
async function getData ({ app, store, defaults }) {
  const data = { ...defaults }
  let browserValues = { ...defaults }
  let countryType

  if (['locale', 'country'].some(key => Object.keys(app.$cookies.getAll()).includes(`ota-${key}`))) {
    data.locale = app.$cookies.get(CLOCALE) || defaults.locale

    countryType = 'cookie'
  } else if (app.context.req) {
    const acceptLanguageHeader = app.context.req.headers['accept-language'] ?? 'nl,en-US;q=0.7,en;q=0.3'
    const browserLocales = acceptLanguageHeader.split(';')[0].split(',')
    browserValues = await getBrowserValues({ app, store, defaults, browserLocales })
    data.locale = browserValues.locale

    countryType = 'browser'
  } else if (process.client && navigator.languages) {
    const browserLocales = navigator.languages
    browserValues = await getBrowserValues({ app, store, defaults, browserLocales })
    data.locale = browserValues.locale

    countryType = 'browser'
  }

  if (!store.getters[`auth/${IS_AUTHENTICATED}`]) {
    switch (countryType) {
      case 'cookie':
        data.country = app.$cookies.get(CCOUNTRY) || defaults.country
        break

      case 'browser':
        data.country = browserValues.country
        break
    }
  } else {
    data.country = store.getters[`auth/${USER_COUNTRY}`]
  }

  return data
}

/**
 * Redirects the user to the right url
 *
 * @param app
 * @param store
 * @param defaults
 * @param browserLocales
 * @returns {*}
 */
async function getBrowserValues ({ app, store, defaults, browserLocales }) {
  let locale = defaults.locale
  let country = defaults.country
  const { app: state } = store.state

  // Get browser language
  browserLocales.some((value) => {
    const newLocale = value.split('-')[0]

    if (!state.locales.includes(newLocale)) {
      return false
    }

    locale = newLocale
    return true
  })

  // Get country based on ip
  if (process.server) {
    try {
      const geoIp = require('geoip-lite')
      const geo = geoIp.lookup(requestIp.getClientIp(app.context.req))

      if (geo) {
        const geoCountry = geo.country.toLowerCase()

        if (state.countries.includes(geoCountry)) {
          country = geoCountry
        }
      }
    } catch (exception) {
      logError(exception)
    }
  }

  if (!process.server) {
    try {
      const response = await fetch('https://get.geojs.io/v1/ip/country.json') // TODO: move to actions
      const { country: geoCountry } = await response.json()

      const lowerCaseGeoCountry = geoCountry.toLowerCase()

      if (state.countries.includes(lowerCaseGeoCountry)) {
        country = lowerCaseGeoCountry
      }
    } catch (e) {
      logError(e)
    }
  }

  return {
    locale,
    country
  }
}

/**
 * Stores the data
 *
 * @param app
 * @param store
 * @param locale
 * @param country
 */
function storeData ({ app, store, data: { locale, country } }) {
  // Set store values
  store.commit(`app/${SET_LANGUAGE}`, locale)
  store.commit(`app/${SET_COUNTRY}`, country)

  // Set i18n
  app.i18n.locale = locale

  // Set cookies
  app.$cookies.set(CLOCALE, locale, {
    path: '/',
    maxAge: cookieConfig.maxAge,
    domain: getCookieDomain()
  })
  app.$cookies.set(CHATBOTLOCALE, locale, {
    path: '/',
    maxAge: cookieConfig.maxAge,
    domain: getCookieDomain()
  })
  app.$cookies.set(CCOUNTRY, country, {
    path: '/',
    maxAge: cookieConfig.maxAge,
    domain: getCookieDomain()
  })
}
